import { Shul, ZmanimData, ResolvedScheduleTime } from '../types';
import { detectDayType } from './dayTypeDetector';
import { resolveAllSchedules } from './scheduleResolver';
import { applyOffset } from './timeUtils';

const GRACE_MINUTES = 10;
const EVENING_THRESHOLD = '17:00';

export interface DashboardSchedule {
  todayRows: ResolvedScheduleTime[];
  tomorrowRows: ResolvedScheduleTime[];
  showBreaker: boolean;
  nextKey: string | null;
}

export function scheduleRowKey(m: ResolvedScheduleTime): string {
  return `${m.shulId}-${m.scheduleId}-${m.prayerType}-${m.displayTime}`;
}

export function buildDashboardSchedule(
  shuls: Shul[],
  todayStr: string,
  tomorrowStr: string,
  todayZmanim: ZmanimData | null,
  tomorrowZmanim: ZmanimData | null,
  now: string
): DashboardSchedule {
  if (!todayZmanim) {
    return { todayRows: [], tomorrowRows: [], showBreaker: false, nextKey: null };
  }

  const todayDayType = detectDayType(todayZmanim);
  const allToday = resolveAllSchedules(shuls, todayStr, todayDayType, todayZmanim)
    .sort((a, b) => a.displayTime.localeCompare(b.displayTime));

  // A time stays visible until 10 minutes after its start time.
  const todayRows = allToday.filter((m) => applyOffset(m.displayTime, GRACE_MINUTES) >= now);

  let tomorrowRows: ResolvedScheduleTime[] = [];
  if (tomorrowZmanim) {
    const tomorrowDayType = detectDayType(tomorrowZmanim);
    const allTomorrow = resolveAllSchedules(shuls, tomorrowStr, tomorrowDayType, tomorrowZmanim)
      .sort((a, b) => a.displayTime.localeCompare(b.displayTime));

    if (todayRows.length === 0) {
      // Today's schedule is entirely done (past its grace period) — roll over completely.
      tomorrowRows = allTomorrow;
    } else if (now >= EVENING_THRESHOLD) {
      // Evening preview: show tomorrow's Shacharit alongside what's left of today.
      tomorrowRows = allTomorrow.filter((m) => m.prayerType === 'shacharit');
    }
  }

  const nextKey =
    todayRows.length > 0
      ? scheduleRowKey(todayRows[0])
      : tomorrowRows.length > 0
      ? scheduleRowKey(tomorrowRows[0])
      : null;

  return { todayRows, tomorrowRows, showBreaker: tomorrowRows.length > 0, nextKey };
}
